/**
 * 1.3.0 the portal app's building blocks: data loading, state chips, the drop zone with progress and resume, the
 * rights declaration, the metadata form, the comment threads and a confirmation dialog.
 */
import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { t } from './i18n/en';
import { errorText, portalApi } from './client';
import { formatBytes, formatDateTime, formatDuration, formatPercent } from './format';
import { pendingFor, dropPending, sameFile, startUpload, subscribeUploads, uploadFor, readPending, type PendingUpload, type PortalUpload, type UploadResult, type UploadTarget } from './uploader';
import type { PortalNote, PortalSession, SupplierField } from './types';

/** Who is signed in and the portal's branding (main.tsx provides it). */
export const SessionContext = createContext<PortalSession | null>(null);
export const useSession = () => useContext(SessionContext);

/* ---------------------------------------------------------------- data */

/** Loads `path`, again every `pollMs` while `poll` says so; `reload` refetches. */
export function useLoad<T>(path: string | null, opts: { poll?: (v: T) => boolean; pollMs?: number } = {}) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [tick, setTick] = useState(0);
  const pollRef = useRef(opts.poll);
  pollRef.current = opts.poll;
  useEffect(() => {
    if (!path) return;
    let stop = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = async () => {
      try {
        const v = await portalApi<T>(path);
        if (stop) return;
        setData(v);
        setError(null);
        if (pollRef.current?.(v)) timer = setTimeout(load, opts.pollMs ?? 4000);
      } catch (err) {
        if (!stop) setError(err);
      }
    };
    void load();
    return () => { stop = true; clearTimeout(timer); };
  }, [path, tick, opts.pollMs]);
  const reload = useCallback(() => setTick((n) => n + 1), []);
  return { data, error, reload };
}

export function Loading({ error, retry }: { error?: unknown; retry?: () => void }) {
  if (error) {
    return (
      <div className="p-notice p-notice-error" role="alert">
        <p>{errorText(error)}</p>
        {retry ? <button type="button" className="p-btn" onClick={retry}>{t('common.retry')}</button> : null}
      </div>
    );
  }
  return <p className="p-muted" aria-live="polite">{t('common.loading')}</p>;
}

/* ---------------------------------------------------------------- chips */

const TONE: Record<string, string> = {
  open: 'neutral', in_progress: 'busy', in_review: 'busy', accepted: 'good', cancelled: 'neutral', expired: 'neutral',
  uploaded: 'busy', rejected: 'bad', waived: 'neutral', draft: 'neutral', waiting: 'busy', needs_changes: 'bad', withdrawn: 'neutral',
  passed: 'good', warnings: 'warn', failed: 'bad', error: 'bad', queued: 'busy', running: 'busy',
};
export function Chip({ kind, state }: { kind: 'assignment' | 'item' | 'submission' | 'qc'; state: string }) {
  const label = kind === 'qc' ? t(`portal.qc.${state}`) : t(`portal.state.${kind}.${state}`);
  return <span className={`p-chip p-chip-${TONE[state] ?? 'neutral'}`} data-state={state}>{label}</span>;
}

/* ---------------------------------------------------------------- uploading */

function useUpload(key: string): PortalUpload | null {
  const [, force] = useState(0);
  useEffect(() => subscribeUploads(() => force((n) => n + 1)), []);
  const u = uploadFor(key);
  useEffect(() => (u ? u.subscribe(() => force((n) => n + 1)) : undefined), [u]);
  return u;
}

/** An upload's progress: the bar, done of total, speed and time left; or what stopped it. */
export function UploadProgress({ upload }: { upload: PortalUpload }) {
  const pct = upload.total ? Math.min(100, (upload.sent / upload.total) * 100) : 0;
  const left = upload.speed > 0 ? (upload.total - upload.sent) / upload.speed : NaN;
  return (
    <div className="p-progress" data-testid="upload-progress" data-state={upload.state}>
      <div className="p-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)} aria-label={upload.file.name}>
        <span style={{ width: `${pct}%` }} />
      </div>
      <div className="p-progress-text">
        <span className="p-file">{upload.file.name}</span>
        {upload.state === 'sending' || upload.state === 'starting' ? (
          <span>
            {t('portal.upload.progress', { done: formatBytes(upload.sent), total: formatBytes(upload.total) })}
            {upload.speed > 0 ? ` · ${t('portal.upload.speed', { speed: formatBytes(upload.speed) })} · ${t('portal.upload.remaining', { time: formatDuration(left) })}` : ''}
          </span>
        ) : null}
        {upload.state === 'offline' ? <span className="p-warn">{t('portal.upload.paused')}</span> : null}
        {upload.state === 'completing' ? <span>{t('portal.upload.checking')}</span> : null}
        {upload.state === 'done' ? <span className="p-good">{t('portal.upload.done')}</span> : null}
        {upload.state === 'failed' ? <span className="p-bad" role="alert">{t('portal.upload.failed', { message: errorText(upload.error) })}</span> : null}
      </div>
      {upload.active && upload.state !== 'completing' ? (
        <button type="button" className="p-btn p-btn-quiet p-btn-small" onClick={() => void upload.cancel()}>{t('portal.upload.cancel')}</button>
      ) : null}
    </div>
  );
}

/** Asks for the file an interrupted upload was for, and continues it from the server's offset. */
export function ResumeCard({ pending, onDone }: { pending: PendingUpload; onDone?: (r: UploadResult) => void }) {
  const [wrong, setWrong] = useState(false);
  const [offset, setOffset] = useState<number | null>(null);
  const [gone, setGone] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    portalApi<{ receivedBytes: number; status: string }>(`/uploads/${pending.id}`)
      .then((u) => { if (u.status !== 'open') { dropPending(pending.key); setGone(true); } else setOffset(u.receivedBytes); })
      .catch(() => { dropPending(pending.key); setGone(true); });
  }, [pending]);
  if (gone || offset === null) return null;
  return (
    <div className="p-resume" data-testid="resume-card">
      <strong>{t('portal.upload.resumeTitle')}</strong>
      <p>{t('portal.upload.resumeBody', { name: pending.name, size: formatBytes(pending.size), percent: formatPercent(offset, pending.size) })}</p>
      {wrong ? <p className="p-bad" role="alert">{t('portal.upload.resumeWrongFile', { name: pending.name, size: formatBytes(pending.size) })}</p> : null}
      <div className="p-row">
        <button type="button" className="p-btn p-btn-primary" onClick={() => input.current?.click()}>{t('portal.upload.resumeChoose')}</button>
        <button type="button" className="p-btn p-btn-quiet" onClick={() => { dropPending(pending.key); void portalApi(`/uploads/${pending.id}`, { method: 'DELETE' }).catch(() => undefined); setGone(true); }}>{t('portal.upload.resumeDiscard')}</button>
      </div>
      <input
        ref={input} type="file" hidden data-testid="resume-input"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (!f) return;
          if (!sameFile(pending, f)) { setWrong(true); return; }
          startUpload(pending.key, f, pending.target, { resumeId: pending.id, onDone });
        }}
      />
    </div>
  );
}

const acceptOf = (types: string[]) => (types.length ? types.map((x) => `.${x}`).join(',') : undefined);

/**
 * A drop zone and file buttons: drag and drop, choose a file, and on phones the photo and video library. One file
 * per key (`multiple` hands every file to onFiles instead).
 */
export function DropZone({ acceptedTypes, media, multiple, onFiles, label, disabled }: {
  acceptedTypes: string[]; media: boolean; multiple?: boolean; onFiles: (files: File[]) => void; label?: string; disabled?: boolean;
}) {
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const library = useRef<HTMLInputElement>(null);
  const id = useId();
  const take = (list: FileList | null) => {
    const files = [...(list ?? [])];
    if (files.length) onFiles(multiple ? files : files.slice(0, 1));
  };
  return (
    <div
      className={`p-drop${over ? ' over' : ''}${disabled ? ' disabled' : ''}`}
      data-testid="dropzone"
      onDragOver={(e) => { if (disabled) return; e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); if (!disabled) take(e.dataTransfer.files); }}
    >
      <span className="p-drop-text" id={`${id}-l`}>{label ?? (multiple ? t('portal.upload.dropMany') : t('portal.upload.drop'))}</span>
      <div className="p-row">
        <button type="button" className="p-btn" disabled={disabled} onClick={() => input.current?.click()} aria-describedby={`${id}-l`}>
          {multiple ? t('portal.upload.chooseMany') : t('portal.upload.choose')}
        </button>
        {media ? (
          <button type="button" className="p-btn p-btn-quiet p-only-touch" disabled={disabled} onClick={() => library.current?.click()}>{t('portal.upload.camera')}</button>
        ) : null}
      </div>
      <input ref={input} type="file" hidden multiple={multiple} accept={acceptOf(acceptedTypes)} data-testid="file-input" onChange={(e) => { take(e.target.files); e.target.value = ''; }} />
      {media ? <input ref={library} type="file" hidden multiple={multiple} accept="video/*,image/*,audio/*" onChange={(e) => { take(e.target.files); e.target.value = ''; }} /> : null}
    </div>
  );
}

/** An item's upload: the progress of a running one, the resume card of an interrupted one, or the drop zone. */
export function ItemUploader({ uploadKey, target, acceptedTypes, media, onDone, label }: {
  uploadKey: string; target: UploadTarget; acceptedTypes: string[]; media: boolean; onDone: (r: UploadResult) => void; label?: string;
}) {
  const upload = useUpload(uploadKey);
  const pending = upload?.active ? null : pendingFor(uploadKey);
  if (upload && (upload.active || upload.state === 'failed')) {
    return (
      <div className="p-uploader">
        <UploadProgress upload={upload} />
        {upload.state === 'failed' ? <DropZone acceptedTypes={acceptedTypes} media={media} label={label} onFiles={([f]) => startUpload(uploadKey, f!, target, { onDone })} /> : null}
      </div>
    );
  }
  if (pending) return <ResumeCard pending={pending} onDone={onDone} />;
  return (
    <div className="p-uploader">
      {upload?.state === 'done' ? <UploadProgress upload={upload} /> : null}
      <DropZone acceptedTypes={acceptedTypes} media={media} label={label} onFiles={([f]) => startUpload(uploadKey, f!, target, { onDone })} />
    </div>
  );
}

/** Interrupted open uploads (New upload): their resume cards. */
export function OpenResumeCards({ onDone }: { onDone?: (r: UploadResult) => void }) {
  const [, force] = useState(0);
  useEffect(() => subscribeUploads(() => force((n) => n + 1)), []);
  const list = readPending().filter((p) => p.key.startsWith('open:') && !uploadFor(p.key)?.active);
  const running = readPending().filter((p) => p.key.startsWith('open:')).map((p) => uploadFor(p.key)).filter((u): u is PortalUpload => Boolean(u?.active));
  return (
    <>
      {running.map((u) => <UploadProgress key={u.key} upload={u} />)}
      {list.map((p) => <ResumeCard key={p.key} pending={p} onDone={onDone} />)}
    </>
  );
}

/* ---------------------------------------------------------------- forms */

export function RightsBox({ text, checked, onChange }: { text: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <fieldset className="p-rights">
      <legend>{t('portal.rights.title')}</legend>
      <p className="p-pre">{text || t('portal.rights.default')}</p>
      <label className="p-check">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid="rights-accept" />
        <span>{t('portal.rights.accept')}</span>
      </label>
    </fieldset>
  );
}

/** The metadata fields a spec or rule asks for. */
export function FieldsForm({ fields, values, onChange }: { fields: SupplierField[]; values: Record<string, string>; onChange: (v: Record<string, string>) => void }) {
  return (
    <div className="p-fields">
      {fields.map((f) => {
        const id = `pf-${f.id}`;
        const set = (v: string) => onChange({ ...values, [f.id]: v });
        return (
          <label key={f.id} htmlFor={id} className="p-field">
            <span>{f.label}{f.required ? <span className="p-muted"> ({t('portal.required')})</span> : null}</span>
            {f.options?.length ? (
              <select id={id} value={values[f.id] ?? ''} onChange={(e) => set(e.target.value)} required={f.required}>
                <option value="" />
                {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : f.type === 'longtext' || f.type === 'richtext' || f.id === 'f_description' ? (
              <textarea id={id} rows={3} value={values[f.id] ?? ''} onChange={(e) => set(e.target.value)} required={f.required} />
            ) : (
              <input id={id} type={f.type === 'date' ? 'date' : f.type === 'number' ? 'number' : 'text'} value={values[f.id] ?? ''} onChange={(e) => set(e.target.value)} required={f.required} />
            )}
            {f.help ? <small className="p-muted">{f.help}</small> : null}
          </label>
        );
      })}
    </div>
  );
}

/** The first required field left empty, or null. */
export const missingField = (fields: SupplierField[], values: Record<string, string>) => fields.find((f) => f.required && !(values[f.id] ?? '').trim()) ?? null;

/* ---------------------------------------------------------------- comments */

/** One thread (the submission's, or an item's) with its composer; a reply is a comment in the same thread. */
export function Thread({ submissionId, itemId, notes, onPosted, title, label }: { submissionId: string; itemId: string | null; notes: PortalNote[]; onPosted: () => void; title: string; label?: string }) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLTextAreaElement>(null);
  const mine = notes.filter((n) => (n.itemId ?? null) === itemId);
  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await portalApi(`/submissions/${submissionId}/notes`, { method: 'POST', json: { body: body.trim(), itemId } });
      setBody('');
      setErr(null);
      onPosted();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };
  // An item's thread without comments stays folded: one line per item instead of an empty box each.
  if (itemId && mine.length === 0 && !open) {
    return (
      <div className="p-thread p-thread-folded" data-testid={`thread-${itemId}`}>
        <button type="button" className="p-link" onClick={() => setOpen(true)}>{t('portal.comments.add', { item: label ?? title })}</button>
      </div>
    );
  }
  return (
    <section className="p-thread" aria-label={title} data-testid={itemId ? `thread-${itemId}` : 'thread-submission'}>
      <h4>{title}</h4>
      {mine.length === 0 ? <p className="p-muted">{t('portal.comments.empty')}</p> : (
        <ol className="p-notes">
          {mine.map((n) => (
            <li key={n.id} className={`p-note${n.author.kind === 'portal' ? ' mine' : ''}${n.decision ? ' decision' : ''}`}>
              <div className="p-note-head">
                <strong>{n.author.kind === 'portal' ? n.author.name : n.author.name}</strong>
                {n.decision ? <span className={`p-chip p-chip-${n.decision === 'rejected' ? 'bad' : n.decision === 'accepted' ? 'good' : 'neutral'}`}>{t(`portal.comments.decision.${n.decision}`)}</span> : null}
                <time className="p-muted" dateTime={n.createdAt}>{formatDateTime(n.createdAt)}</time>
              </div>
              {n.body ? <p className="p-pre">{n.body}</p> : null}
              {n.author.kind === 'member' ? <button type="button" className="p-link" onClick={() => box.current?.focus()}>{t('portal.comments.reply')}</button> : null}
            </li>
          ))}
        </ol>
      )}
      <div className="p-composer">
        <textarea ref={box} rows={2} value={body} placeholder={t('portal.comments.placeholder')} aria-label={t('portal.comments.placeholder')} onChange={(e) => setBody(e.target.value)} maxLength={10000} />
        <button type="button" className="p-btn" disabled={busy || !body.trim()} onClick={() => void send()}>{t('portal.comments.send')}</button>
      </div>
      {err ? <p className="p-bad" role="alert">{errorText(err)}</p> : null}
    </section>
  );
}

/* ---------------------------------------------------------------- dialog */

export function Dialog({ open, title, children, onClose }: { open: boolean; title: string; children: ReactNode; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog ref={ref} className="p-dialog" onClose={onClose} aria-label={title}>
      <h3>{title}</h3>
      {children}
    </dialog>
  );
}
